import { Space, Tag, Tooltip } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { formatToDollar } from '../../../../helpers/genericHelper';

const ANNUAL_PLANS = ['ANNUAL', 'MAX_ANNUAL'];

const pluralize = (count, noun) => `${count} more ${noun}${count === 1 ? '' : 's'}`;

// The plan the current limit was sized on. Rows written before `plan` existed only carry the
// interval flag, which is exact for them (they predate Max).
const resolvePlan = (details) => details?.plan || (details?.hasAnnualSubscription ? 'ANNUAL' : 'MONTHLY');

// Credit limit increases come from the incentive streak: statement-based models count current
// statements, V4 counts on-time payments. This reads the stored limit row, so it is what the
// backend will apply at the next recalculation (statement close / payment), not a live estimate.
function IncentiveTag({ details, currentLimitDollars }) {
  if (details?.isDelinquent) {
    return (
      <Tooltip title="Incentive bonuses are paused while the account is delinquent.">
        <Tag color="red">CLI: Paused (delinquent)</Tag>
      </Tooltip>
    );
  }

  if (details?.rule === 'MAX_CREDIT_LIMIT_REACHED') {
    return <Tag color="green">CLI: At max limit</Tag>;
  }

  const nextLimit = details?.nextBonusCreditLimitDollars;
  const milestone = details?.nextBonusPaymentMilestone;
  if (!nextLimit || !milestone || nextLimit <= currentLimitDollars) {
    return <Tag>CLI: No upcoming bonus</Tag>;
  }

  const isStatementBased = details?.model?.startsWith('STATEMENT_BASED');
  const remaining = Math.max(1, milestone - (details?.consecutiveCcPayments || 0));
  const unit = isStatementBased ? 'current statement' : 'on-time payment';
  const tooltip = isStatementBased
    ? 'Applied when the statement closes, as long as the account is not 30+ days past due. The opening statement does not count.'
    : 'Applied after the on-time payment posts.';

  return (
    <Tooltip title={tooltip}>
      <Tag style={{ fontWeight: 'bold' }} color="blue">
        Next CLI: {formatToDollar(nextLimit)} after {pluralize(remaining, unit)}
      </Tag>
    </Tooltip>
  );
}

// `annualCliEligibility` only answers "would switching to annual raise the limit?", so it is
// meaningless for users already on annual or Max and is hidden for them.
function AnnualUpgradeTag({ annualCliEligibility }) {
  if (!annualCliEligibility) {
    return null;
  }

  const { isEligible, increasePercentage } = annualCliEligibility;
  if (isEligible) {
    const increase = Math.round((increasePercentage || 0) * 100);
    return <Tag color="blue">Annual upgrade: +{increase}% limit</Tag>;
  }

  return <Tag>Annual upgrade: no limit boost</Tag>;
}

export default function CliEligibilitySummary({ creditCard }) {
  const cardDomain = creditCard?.domain;
  if (!cardDomain) {
    return (
      <Tag icon={<QuestionCircleOutlined />}>
        CLI status unknown
      </Tag>
    );
  }

  if (!cardDomain.creditLimitIncentivesEnabled) {
    return (
      <Tooltip title="This card is not enrolled in credit limit incentives, so its limit does not grow with on-time activity.">
        <Tag>CLI: Not enrolled in incentives</Tag>
      </Tooltip>
    );
  }

  const details = cardDomain.creditLimitDetailsBundle?.creditLimitDetails;
  const isAnnual = ANNUAL_PLANS.includes(resolvePlan(details));

  return (
    <Space direction="vertical" size={4}>
      <IncentiveTag details={details} currentLimitDollars={creditCard?.account?.creditLimitInDollars} />
      {!isAnnual && <AnnualUpgradeTag annualCliEligibility={creditCard?.annualCliEligibility} />}
    </Space>
  );
}
